import { lineItemFields } from '../state/fields/line-item';
import { quantityBreakFields } from '../state/fields/quantity-break';
import type { FieldColumn } from '../types/column';

export const lineItemStickyLeadingTrackSize = '60px';

export const lineItemColumns: FieldColumn[] = [
  {
    label: 'Line #',
    trackSize: '50px',
    fieldId: lineItemFields.lineNumber,
  },
  {
    label: 'Description',
    trackSize: '250px',
    fieldId: lineItemFields.description,
  },
  {
    label: 'Drawing Number',
    trackSize: '100px',
    fieldId: lineItemFields.drawingId,
  },
  {
    label: 'Rev.',
    trackSize: '75px',
    fieldId: lineItemFields.drawingRevisionNo,
  },
  {
    label: 'Customer Part Number',
    trackSize: '125px',
    fieldId: lineItemFields.customerPartId,
  },
  {
    label: 'Quantity',
    trackSize: '100px',
    fieldId: quantityBreakFields.quantity,
  },
  {
    label: 'Material',
    trackSize: '150px',
    fieldId: lineItemFields.material,
  },
  {
    label: 'DFARS',
    trackSize: '100px',
    fieldId: lineItemFields.dfars,
  },
  {
    label: 'Fastener Type',
    trackSize: '200px',
    fieldId: lineItemFields.fastenerType,
  },
  {
    label: 'Thread Size',
    trackSize: '100px',
    fieldId: lineItemFields.threadSize,
  },
  {
    label: 'Pitch/TPI',
    trackSize: '100px',
    fieldId: lineItemFields.threadPitch,
  },
  {
    label: 'Thread Length',
    trackSize: '115px',
    fieldId: lineItemFields.length,
  },
  {
    label: 'Shoulder Diameter',
    trackSize: '115px',
    fieldId: lineItemFields.shoulderDiameter,
  },
  {
    label: 'Shoulder Length',
    trackSize: '115px',
    fieldId: lineItemFields.shoulderLength,
  },
  {
    label: 'Head Style',
    trackSize: '175px',
    fieldId: lineItemFields.headStyle,
  },
  {
    label: 'Recess Style',
    trackSize: '175px',
    fieldId: lineItemFields.recess,
  },
  {
    label: 'Serration',
    trackSize: '100px',
    fieldId: lineItemFields.serration,
  },
  {
    label: 'Surface Finish',
    trackSize: '150px',
    fieldId: lineItemFields.surfaceFinish,
  },
  {
    label: 'Post Finish',
    trackSize: '150px',
    fieldId: lineItemFields.postFinish,
  },
  {
    label: 'OSP 2',
    trackSize: '125px',
    fieldId: lineItemFields.osp2,
  },
  {
    label: 'OSP 2 Description',
    trackSize: '125px',
    fieldId: lineItemFields.osp2Description,
  },
  {
    label: 'OSP 2 Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.osp2Price,
  },
  {
    label: 'OSP 2 Min Lot',
    trackSize: '100px',
    fieldId: lineItemFields.osp2MinLot,
  },
  {
    label: 'OSP 2 UOM',
    trackSize: '100px',
    fieldId: lineItemFields.osp2UOM,
  },
  {
    label: 'OSP 3',
    trackSize: '125px',
    fieldId: lineItemFields.osp3,
  },
  {
    label: 'OSP 3 Description',
    trackSize: '125px',
    fieldId: lineItemFields.osp3Description,
  },
  {
    label: 'OSP 3 Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.osp3Price,
  },
  {
    label: 'OSP 3 Min Lot',
    trackSize: '100px',
    fieldId: lineItemFields.osp3MinLot,
  },
  {
    label: 'OSP 3 UOM',
    trackSize: '100px',
    fieldId: lineItemFields.osp3UOM,
  },
  {
    label: 'SEMS Required',
    trackSize: '100px',
    fieldId: lineItemFields.hasSemsEmbed,
  },
  {
    label: 'SEMS 1 Style',
    trackSize: '125px',
    fieldId: lineItemFields.sems1Style,
  },
  {
    label: 'SEMS 1 Weight lbs/ea',
    trackSize: '100px',
    fieldId: lineItemFields.sems1Weight,
  },
  {
    label: 'SEMS 1 Cost $/ea',
    trackSize: '125px',
    fieldId: quantityBreakFields.sems1Cost,
  },
  {
    label: 'SEMS 2 Style',
    trackSize: '125px',
    fieldId: lineItemFields.sems2Style,
  },
  {
    label: 'SEMS 2 Weight lbs/ea',
    trackSize: '100px',
    fieldId: lineItemFields.sems2Weight,
  },
  {
    label: 'SEMS 2 Cost $/ea',
    trackSize: '125px',
    fieldId: quantityBreakFields.sems2Cost,
  },
  {
    label: 'Gross Margin',
    trackSize: '125px',
    fieldId: quantityBreakFields.userGrossMargin,
  },
  {
    label: 'Unit Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.userUnitPrice,
  },
  {
    label: 'Lead Time',
    trackSize: '110px',
    fieldId: quantityBreakFields.userLeadTimeWeeks,
  },
];

const stickyLineItemColumnFieldKeys: ReadonlySet<string> = new Set([
  lineItemFields.lineNumber.key,
  lineItemFields.customerPartId.key,
]);

export const lineItemStickyColumns = lineItemColumns.filter((column) =>
  stickyLineItemColumnFieldKeys.has(column.fieldId.key),
);

export const lineItemScrollableColumns = lineItemColumns.filter(
  (column) => !stickyLineItemColumnFieldKeys.has(column.fieldId.key),
);

export const quantityBreakColumns: FieldColumn[] = [
  {
    label: 'Quantity',
    trackSize: '100px',
    fieldId: quantityBreakFields.quantity,
  },
  {
    label: 'SEMS 1 Style',
    trackSize: '125px',
    fieldId: lineItemFields.sems1Style,
  },
  {
    label: 'SEMS 1 Cost',
    trackSize: '125px',
    fieldId: quantityBreakFields.sems1Cost,
  },
  {
    label: 'SEMS 2 Style',
    trackSize: '125px',
    fieldId: lineItemFields.sems2Style,
  },
  {
    label: 'SEMS 2 Cost',
    trackSize: '125px',
    fieldId: quantityBreakFields.sems2Cost,
  },
  {
    label: 'OSP 2',
    trackSize: '125px',
    fieldId: lineItemFields.osp2,
  },
  {
    label: 'OSP 2 Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.osp2Price,
  },
  {
    label: 'OSP 3',
    trackSize: '125px',
    fieldId: lineItemFields.osp3,
  },
  {
    label: 'OSP 3 Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.osp3Price,
  },
  {
    label: 'Gross Margin',
    trackSize: '125px',
    fieldId: quantityBreakFields.userGrossMargin,
  },
  {
    label: 'Unit Price',
    trackSize: '125px',
    fieldId: quantityBreakFields.userUnitPrice,
  },
  {
    label: 'Lead Time',
    trackSize: '110px',
    fieldId: quantityBreakFields.userLeadTimeWeeks,
  },
];
